/**
 * React Query mutations for task CRUD operations
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 292: Delete tasks from web UI with coverage warning
 * Story 356: Web event instrumentation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { storyTaskKeys, type TaskListResponse } from './useStoryTasks';
import { track } from '../lib/analytics';

/** Task update payload */
interface TaskUpdate {
  title?: string;
  body?: string;
  task_type?: string;
  implements?: string[];
  status?: 'proposed' | 'in-progress' | 'done';
}

/** Task create payload */
interface TaskCreate {
  title: string;
  body?: string;
  task_type?: string;
  implements?: string[];
}

/** API mutation result */
interface TaskMutationResult {
  success: boolean;
  message?: string;
  seq?: number;
}

/**
 * Hook for task mutations
 *
 * Provides mutations for:
 * - updateTask: Update title, body, task_type, or implements
 * - createTask: Add a new task
 * - reorderTasks: Change task display order
 */
export function useTaskMutations(projectSlug: string, storyId: string) {
  const queryClient = useQueryClient();
  const queryKey = storyTaskKeys.list(projectSlug, storyId);

  const updateTask = useMutation({
    mutationFn: async ({
      seq,
      updates,
    }: {
      seq: number;
      updates: TaskUpdate;
    }): Promise<TaskMutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks/${seq}`;
      return api.fetch(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },
    onMutate: async ({ seq, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<TaskListResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData<TaskListResponse>(queryKey, {
          tasks: previousData.tasks.map((task) =>
            task.seq === seq ? { ...task, ...updates } : task
          ),
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (_, { seq, updates }) => {
      // Story 356: Track task update
      track({
        name: 'task_updated',
        properties: {
          story_id: storyId,
          seq,
          fields_changed: Object.keys(updates),
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const createTask = useMutation({
    mutationFn: async (task: TaskCreate): Promise<TaskMutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks`;
      return api.fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(task),
      });
    },
    onSuccess: (data, variables) => {
      // Story 356: Track task creation
      const seq = data.seq || 0; // FALLBACK: Analytics non-blocking - use 0 if missing
      track({
        name: 'task_created',
        properties: {
          story_id: storyId,
          seq,
          task_type: variables.task_type,
        },
      });
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const reorderTasks = useMutation({
    mutationFn: async (order: number[]): Promise<TaskMutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks/reorder`;
      return api.fetch(endpoint, {
        method: 'PATCH', // the worker admits GET, POST, PATCH and DELETE only; PUT is answered 405 before routing
        body: JSON.stringify({ order }),
      });
    },
    onMutate: async (order) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<TaskListResponse>(queryKey);

      if (previousData) {
        // Reorder tasks based on the order array
        const reorderedTasks = order
          .map((seq) => previousData.tasks.find((t) => t.seq === seq))
          .filter((t): t is NonNullable<typeof t> => t !== undefined);

        queryClient.setQueryData<TaskListResponse>(queryKey, {
          tasks: reorderedTasks,
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (_, order) => {
      // Story 356: Track task reorder
      track({
        name: 'task_reordered',
        properties: {
          story_id: storyId,
          new_order: order,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  /**
   * Story 292: Delete a task (soft delete)
   * Does NOT use optimistic updates - waits for server confirmation
   */
  const deleteTask = useMutation({
    mutationFn: async (seq: number): Promise<TaskMutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks/${seq}`;
      return api.fetch(endpoint, {
        method: 'DELETE',
        body: JSON.stringify({}),
      });
    },
    onSuccess: (_, seq) => {
      // Story 356: Track task deletion
      track({
        name: 'task_deleted',
        properties: { story_id: storyId, seq },
      });
      queryClient.invalidateQueries({ queryKey });
    },
  });

  /**
   * Story 292: Restore a deleted task
   */
  const restoreTask = useMutation({
    mutationFn: async (seq: number): Promise<TaskMutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks/${seq}/restore`;
      return api.fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: (_, seq) => {
      // Story 356: Track task restoration
      track({
        name: 'task_restored',
        properties: { story_id: storyId, seq },
      });
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    updateTask,
    createTask,
    reorderTasks,
    deleteTask,
    restoreTask,
  };
}
